import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';

import type { ApiError } from '@/core/api';
import * as api from './api';
import type { EmployeeSkillInput, PDPGoalInput, PDPInput, SkillInput } from './api';

export const devKeys = {
  pdps: ['hrm', 'pdps'] as const,
  pdpList: (p: api.PDPListParams) => [...devKeys.pdps, p] as const,
  pdpDetail: (id: string | number) => [...devKeys.pdps, 'detail', id] as const,
  skills: ['hrm', 'skills'] as const,
  matrix: ['hrm', 'employee-skills'] as const,
  matrixList: (p: api.EmployeeSkillListParams) => [...devKeys.matrix, p] as const,
};

/** Truthy id that isn't the `-1` "skip" sentinel used by optional lookups. */
function isResolvable(id: string | number): boolean {
  return id !== '' && id !== -1 && id !== '-1' && id != null;
}

// PDPs
export function usePdps(params: api.PDPListParams = {}) {
  return useQuery({
    queryKey: devKeys.pdpList(params),
    queryFn: () => api.listPdps(params),
    placeholderData: keepPreviousData,
  });
}

export function usePdp(id: string | number) {
  return useQuery({
    queryKey: devKeys.pdpDetail(id),
    queryFn: () => api.getPdp(id),
    // Resolves by public_id (string, from the URL) or integer pk; the backend
    // accepts either (ADR-041).
    enabled: isResolvable(id),
  });
}

export function useCreatePdp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PDPInput) => api.createPdp(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: devKeys.pdps });
      toast.success('PDP created');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useUpdatePdp(id: string | number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<PDPInput>) => api.updatePdp(id, input),
    onSuccess: (pdp) => {
      qc.invalidateQueries({ queryKey: devKeys.pdps });
      qc.setQueryData(devKeys.pdpDetail(id), pdp);
      toast.success('PDP updated');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useDeletePdp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => api.deletePdp(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: devKeys.pdps });
      toast.success('PDP deleted');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useCreatePdpGoal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PDPGoalInput) => api.createPdpGoal(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: devKeys.pdps }),
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useUpdatePdpGoal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: Partial<PDPGoalInput> }) =>
      api.updatePdpGoal(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: devKeys.pdps }),
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useDeletePdpGoal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.deletePdpGoal(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: devKeys.pdps }),
    onError: (err: ApiError) => toast.error(err.message),
  });
}

// Skills
export function useSkills() {
  return useQuery({ queryKey: devKeys.skills, queryFn: () => api.listSkills() });
}

export function useCreateSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SkillInput) => api.createSkill(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: devKeys.skills });
      toast.success('Skill created');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useDeleteSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.deleteSkill(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: devKeys.skills });
      qc.invalidateQueries({ queryKey: devKeys.matrix });
      toast.success('Skill removed');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

// Employee skills (matrix)
export function useEmployeeSkills(params: api.EmployeeSkillListParams = {}) {
  return useQuery({
    queryKey: devKeys.matrixList(params),
    queryFn: () => api.listEmployeeSkills(params),
    placeholderData: keepPreviousData,
  });
}

export function useCreateEmployeeSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: EmployeeSkillInput) => api.createEmployeeSkill(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: devKeys.matrix }),
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useUpdateEmployeeSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: Partial<EmployeeSkillInput> }) =>
      api.updateEmployeeSkill(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: devKeys.matrix }),
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useDeleteEmployeeSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.deleteEmployeeSkill(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: devKeys.matrix }),
    onError: (err: ApiError) => toast.error(err.message),
  });
}
